/**
 * The re-open button can be dragged by the visitor (it may cover something on the page). It snaps to the nearest
 * side and keeps its height; the place is remembered on this device in localStorage, which only happens after the
 * visitor moves it themselves (a feature they asked for, so no consent is needed). Clicks still open the preferences.
 */

export const REOPEN_POSITION_KEY = "BerryCookieReopen";

/** Distance from the edges, as in the stylesheet. */
const MARGIN = 16;
/** Movement (px) after which a press becomes a drag instead of a click. */
const DRAG_THRESHOLD = 6;

export interface ReopenPosition {
  side: "left" | "right";
  /** Distance from the bottom of the window, in px. */
  bottom: number;
}

export function readReopenPosition(win: Window): ReopenPosition | null {
  try {
    const raw = win.localStorage.getItem(REOPEN_POSITION_KEY);
    if (!raw) {
      return null;
    }
    const value = JSON.parse(raw) as Partial<ReopenPosition>;
    return (value.side === "left" || value.side === "right") &&
      typeof value.bottom === "number" &&
      Number.isFinite(value.bottom)
      ? { side: value.side, bottom: value.bottom }
      : null;
  } catch {
    return null;
  }
}

function saveReopenPosition(win: Window, position: ReopenPosition): void {
  try {
    win.localStorage.setItem(REOPEN_POSITION_KEY, JSON.stringify(position));
  } catch {
    // Storage can be blocked; the button then stays where it was dropped until the page is left.
  }
}

/** Keeps the button inside the window, whatever its size now. */
export function clampBottom(bottom: number, windowHeight: number, size: number): number {
  return Math.round(Math.min(Math.max(MARGIN, bottom), Math.max(MARGIN, windowHeight - size - MARGIN)));
}

function apply(button: HTMLElement, position: ReopenPosition, win: Window): void {
  const size = button.offsetHeight || 44;
  button.style.top = "";
  button.style.left = position.side === "left" ? `${MARGIN}px` : "auto";
  button.style.right = position.side === "right" ? `${MARGIN}px` : "auto";
  button.style.bottom = `${clampBottom(position.bottom, win.innerHeight, size)}px`;
}

/** Makes the button draggable; returns a function that removes the listeners. */
export function makeDraggable(button: HTMLElement, win: Window): () => void {
  let position = readReopenPosition(win);
  if (position) {
    apply(button, position, win);
  }

  let start: { x: number; y: number; left: number; top: number } | null = null;
  let dragging = false;
  let suppressClick = false;

  const onDown = (event: PointerEvent) => {
    if (event.button !== 0) {
      return;
    }
    const rect = button.getBoundingClientRect();
    start = { x: event.clientX, y: event.clientY, left: rect.left, top: rect.top };
    dragging = false;
    button.setPointerCapture?.(event.pointerId);
  };

  const onMove = (event: PointerEvent) => {
    if (!start) {
      return;
    }
    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;
    if (!dragging && Math.hypot(dx, dy) < DRAG_THRESHOLD) {
      return;
    }
    dragging = true;
    button.classList.add("bc-dragging");
    const size = button.offsetWidth || 44;
    const left = Math.min(Math.max(0, start.left + dx), win.innerWidth - size);
    const top = Math.min(Math.max(0, start.top + dy), win.innerHeight - size);
    button.style.left = `${left}px`;
    button.style.top = `${top}px`;
    button.style.right = "auto";
    button.style.bottom = "auto";
    event.preventDefault();
  };

  const onUp = (event: PointerEvent) => {
    if (!start) {
      return;
    }
    button.releasePointerCapture?.(event.pointerId);
    start = null;
    if (!dragging) {
      return;
    }
    dragging = false;
    suppressClick = true;
    button.classList.remove("bc-dragging");
    // Glide to the edge after a drop only; the saved place is applied at once when the page loads.
    button.classList.add("bc-snap");
    win.setTimeout(() => button.classList.remove("bc-snap"), 250);
    const rect = button.getBoundingClientRect();
    position = {
      side: rect.left + rect.width / 2 < win.innerWidth / 2 ? "left" : "right",
      bottom: win.innerHeight - rect.bottom,
    };
    apply(button, position, win);
    saveReopenPosition(win, position);
  };

  // A drag ends with a click on the button; it must not open the preferences.
  const onClick = (event: MouseEvent) => {
    if (suppressClick) {
      suppressClick = false;
      event.stopImmediatePropagation();
      event.preventDefault();
    }
  };

  const onResize = () => {
    if (position) {
      apply(button, position, win);
    }
  };

  button.addEventListener("pointerdown", onDown);
  button.addEventListener("pointermove", onMove);
  button.addEventListener("pointerup", onUp);
  button.addEventListener("pointercancel", onUp);
  button.addEventListener("click", onClick, true);
  win.addEventListener("resize", onResize);
  return () => {
    button.removeEventListener("pointerdown", onDown);
    button.removeEventListener("pointermove", onMove);
    button.removeEventListener("pointerup", onUp);
    button.removeEventListener("pointercancel", onUp);
    button.removeEventListener("click", onClick, true);
    win.removeEventListener("resize", onResize);
  };
}
